import { useTranslate } from "ra-core";
import { ArrowDown, ArrowUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  ACTION_TYPES,
  type ActionSpec,
  type ActionType,
  type Delay,
} from "../../../../supabase/functions/_shared/automation/types.ts";
import { ACTION_PARAMS, type ParamDef, withCurrent } from "./ruleModel";
import { DelayPicker } from "./DelayPicker";
import { selectClass } from "./ConditionBuilder";
import { useRowFocus } from "./useRowFocus";
import { useDealStages } from "../deals/useDealStages";
import { useStageLabel } from "../deals/useStageLabel";
import { useLeadStatuses } from "../leads/useLeadStatuses";
import { useLeadStatusLabel } from "../leads/useLeadStatusLabel";

const CHANNELS = ['["email"]', '["phone"]', '["phone","email"]'];

/** create_deal / move_deal: the configured stages (config.dealStages), with their labels. */
const StageParam = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: unknown;
  onChange: (v: unknown) => void;
}) => {
  const stages = useDealStages();
  const stageLabel = useStageLabel();
  return (
    <select
      aria-label={label}
      className={selectClass}
      value={String(value ?? "")}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="" />
      {withCurrent(
        stages.map((s) => s.value),
        typeof value === "string" ? [value] : [],
      ).map((v) => (
        <option key={v} value={v}>
          {stageLabel(v)}
        </option>
      ))}
    </select>
  );
};

/** set_prospect_status: the configured lead statuses (config.leadStatuses), with their labels; a stale value stays shown. */
const LeadStatusParam = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: unknown;
  onChange: (v: unknown) => void;
}) => {
  const statuses = useLeadStatuses();
  const statusLabel = useLeadStatusLabel();
  return (
    <select
      aria-label={label}
      className={selectClass}
      value={String(value ?? "")}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="" />
      {withCurrent(
        statuses.map((s) => s.value),
        typeof value === "string" ? [value] : [],
      ).map((v) => (
        <option key={v} value={v}>
          {statusLabel(v)}
        </option>
      ))}
    </select>
  );
};

const Param = ({
  def,
  value,
  prefix,
  onChange,
}: {
  def: ParamDef;
  value: unknown;
  prefix: string;
  onChange: (v: unknown) => void;
}) => {
  const t = useTranslate();
  const label = `${prefix} — ${t(`gavion.automation.params.${def.key}`, { _: def.key })}`;
  switch (def.kind) {
    case "stage":
      return <StageParam label={label} value={value} onChange={onChange} />;
    case "lead_status":
      return (
        <LeadStatusParam label={label} value={value} onChange={onChange} />
      );
    case "enum":
      return (
        <select
          aria-label={label}
          className={selectClass}
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="" />
          {def.values!.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      );
    case "template":
      return (
        <Textarea
          aria-label={label}
          rows={2}
          className="w-full"
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "delay":
      return (
        <DelayPicker
          label={label}
          value={value as Delay | undefined}
          allowNow={false}
          onChange={onChange}
        />
      );
    case "channels": {
      const current = JSON.stringify(value ?? ["email"]);
      return (
        <select
          aria-label={label}
          className={selectClass}
          value={current}
          onChange={(e) => onChange(JSON.parse(e.target.value))}
        >
          {!CHANNELS.includes(current) && (
            <option value={current}>{current}</option>
          )}
          <option value='["email"]'>email</option>
          <option value='["phone"]'>phone</option>
          <option value='["phone","email"]'>phone + email</option>
        </select>
      );
    }
    case "number":
      return (
        <Input
          aria-label={label}
          type="number"
          className="w-24"
          value={typeof value === "number" ? value : ""}
          onChange={(e) =>
            onChange(e.target.value === "" ? undefined : Number(e.target.value))
          }
        />
      );
    case "time":
      return (
        <Input
          aria-label={label}
          type="time"
          className="w-28"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    default:
      return (
        <Input
          aria-label={label}
          className="w-full sm:w-56"
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
};

const withoutDelay = ({ delay: _delay, ...rest }: ActionSpec): ActionSpec =>
  rest as ActionSpec;

/** Ordered action list: add, remove, reorder, per-type params and a delay (spec 05 §7). */
export const ActionListEditor = ({
  value,
  onChange,
}: {
  value: readonly ActionSpec[];
  onChange: (v: ActionSpec[]) => void;
}) => {
  const t = useTranslate();
  const replace = (i: number, a: ActionSpec) =>
    onChange(value.map((x, j) => (j === i ? a : x)));
  const move = (i: number, d: -1 | 1) =>
    onChange(
      value.map((x, j) =>
        j === i ? value[i + d] : j === i + d ? value[i] : x,
      ),
    );
  const focus = useRowFocus();
  return (
    <div ref={focus.containerRef} className="flex flex-col gap-3">
      {value.map((a, i) => {
        const n = i + 1;
        const prefix = t("gavion.automation.editor.action_n", { n });
        return (
          <div
            key={i}
            role="group"
            aria-label={prefix}
            data-row=""
            className="flex flex-col gap-2 rounded-lg border p-3"
          >
            <div className="flex flex-wrap items-center gap-2">
              <select
                aria-label={`${prefix} — ${t("gavion.automation.editor.action_type")}`}
                className={selectClass}
                value={a.type}
                onChange={(e) =>
                  replace(i, { type: e.target.value as ActionType })
                }
              >
                {!ACTION_TYPES.includes(a.type) && (
                  <option value={a.type}>{String(a.type)}</option>
                )}
                {ACTION_TYPES.map((x) => (
                  <option key={x} value={x}>
                    {t(`gavion.automation.actions.${x}`)}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                disabled={i === 0}
                onClick={() => move(i, -1)}
                aria-label={t("gavion.automation.editor.move_action_up", { n })}
              >
                <ArrowUp className="size-4" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                disabled={i === value.length - 1}
                onClick={() => move(i, 1)}
                aria-label={t("gavion.automation.editor.move_action_down", {
                  n,
                })}
              >
                <ArrowDown className="size-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  focus.afterRemove(i, value.length - 1);
                  onChange(value.filter((_, j) => j !== i));
                }}
              >
                {t("gavion.automation.editor.remove_action", { n })}
              </Button>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              {(ACTION_PARAMS[a.type] ?? []).map((def) => (
                <Param
                  key={def.key}
                  def={def}
                  prefix={prefix}
                  value={a[def.key]}
                  onChange={(v) => replace(i, { ...a, [def.key]: v })}
                />
              ))}
            </div>
            <DelayPicker
              label={`${prefix} — ${t("gavion.automation.delay.label")}`}
              value={a.delay}
              onChange={(delay) =>
                replace(i, delay ? { ...a, delay } : withoutDelay(a))
              }
            />
          </div>
        );
      })}
      <Button
        ref={focus.addRef}
        type="button"
        variant="outline"
        className="self-start"
        onClick={() => {
          focus.afterAdd(value.length);
          onChange([
            ...value,
            {
              type: "create_task",
              text: "",
              due: { minutes: 0 },
              assignee: "rep",
            },
          ]);
        }}
      >
        {t("gavion.automation.editor.add_action")}
      </Button>
    </div>
  );
};
